// Partner session context (B3-PARTNER-COCKPIT). A firm primary-contact session acting
// within a mandated tenant. actingAsPartnerUserId marks the external-party session so
// RBAC applies the mandate-scope intersection (B3-RBAC-CATALOG §2.5). PLAN-C1.11.
import { createContext, useContext, useEffect, type ReactNode } from "react";
import type { SympllexSession } from "@sympllex/auth";
import { setApiSession } from "./api";

export const DEV_SESSION: SympllexSession = {
  user: {
    id: "00000000-0000-0000-0000-000000000020",
    workosUserId: "wos-dev-partner",
    email: "consultant@firm.test",
    fullName: "Dev Consultant",
  },
  activeTenantId: "00000000-0000-0000-0000-0000000000aa",
  availableTenantIds: ["00000000-0000-0000-0000-0000000000aa"],
  roleCodes: ["partner_tenant_primary_contact"],
  actingAsPartnerUserId: "00000000-0000-0000-0000-0000000000f0",
  expiresAt: 4_102_444_800,
};

// The booted session (set by main.tsx after /auth/me, or DEV_SESSION in dev). The provider + the
// router context both read it so RBAC gates + the cockpit see the real signed-in user (C1.3 F1d).
let bootedSession: SympllexSession = DEV_SESSION;
export function setBootedSession(s: SympllexSession): void { bootedSession = s; setApiSession(s); }
export function getBootedSession(): SympllexSession { return bootedSession; }

const SessionCtx = createContext<SympllexSession | null>(null);

export function SessionProvider({ children, session }: { children: ReactNode; session?: SympllexSession }) {
  const active = session ?? getBootedSession();
  useEffect(() => { setApiSession(active); }, [active]);
  return <SessionCtx.Provider value={active}>{children}</SessionCtx.Provider>;
}

export function useSession(): SympllexSession {
  const s = useContext(SessionCtx);
  if (!s) throw new Error("useSession must be used within <SessionProvider>");
  return s;
}
